import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardFloatCalculationType {
    SumSquaredActionDeltas,
    SumSquaredActions,
    TrainingRunComplete,
    Random,
    InitializeRandom,
    ElapsedTime
}

function OperationString(operation : RewardFloatCalculationType) : string {
    if( typeof operation === 'number' )
        return RewardFloatCalculationType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardFloatCalculationType as any)[op];
}
/**
 * Block used to apply math functions
 */
export class RewardFloatCalculation extends RewardGraphNode {
    private currentLockId = -1;
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "SumSquaredActionDeltas", value: RewardFloatCalculationType.SumSquaredActionDeltas },
            { label: "SumSquaredActions", value: RewardFloatCalculationType.SumSquaredActions },
            { label: "TrainingRunComplete", value: RewardFloatCalculationType.TrainingRunComplete },
            { label: "Random", value: RewardFloatCalculationType.Random },
            { label: "InitializeRandom", value: RewardFloatCalculationType.InitializeRandom },
            { label: "ElapsedTime", value: RewardFloatCalculationType.ElapsedTime },
        ],
    })
    public property = RewardFloatCalculationType.SumSquaredActions;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerOutput("value", RewardGraphConnectionPointTypes.Float);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardFloatCalculation";
    }

    /**
     * Gets the left input component
     */
    public get value(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        switch (this.property) {
            case RewardFloatCalculationType.SumSquaredActions: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('sum-squared-actions')
                    return 0;
                };
                break;
            }
            case RewardFloatCalculationType.SumSquaredActionDeltas: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('sum-squared-action-deltas')
                    return 0;
                };
                break;
            }
            case RewardFloatCalculationType.TrainingRunComplete: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('training-run-complete')
                    return 0;
                };
                break;
            }
            case RewardFloatCalculationType.ElapsedTime: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('elapsed-time')
                    return 0;
                };
                break;
            }
            case RewardFloatCalculationType.Random: {
                func = (state) => {
                    return Math.random();
                };
                break;
            }
            case RewardFloatCalculationType.InitializeRandom: {
                const rval = Math.random();
                func = (state) => {
                    return rval;
                };
                break;
            }
        }

        this.value._storedFunction = (state) => {
            const curId = state.buildId || 0;
            if( curId === this.currentLockId )
                return this.value._storedValue;
            this.currentLockId = curId;
            this.value._storedValue = func(state);
            return this.value._storedValue;
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardFloatCalculation.${RewardFloatCalculationType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.property = OperationString(this.property);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardFloatCalculation", RewardFloatCalculation);
